/**
 * Race Result Leaderboard Component
 * 
 * Displays sorted leaderboard for a race event
 * - Shows position, rider name, lap times, total time, notes
 * - Admin/coach edit and delete buttons
 * - Empty state when no results
 * - Responsive table
 * 
 * Requirements: 7.2, 7.3, 7.6, 8.2, 8.5, 9.3, 9.4
 */

import { useCallback, useState } from 'react';
import { restClient, Rider } from '../../../api/rest-client';
import { RaceResult } from '../../../types/raceResults';
import { formatPosition, formatTime } from '../../../utils/raceResults';
import { useAuth } from '../../../auth/AuthProvider';
import { Card } from '../../common/Card';
import { Button } from '../../common/Button';
import { useUIStore } from '../../../store/uiStore';

interface RaceResultLeaderboardProps {
  results: RaceResult[];
  riders: Rider[];
  eventName?: string;
  showRecordButton?: boolean;
  onRecordClick?: () => void;
  onEditClick?: (result: RaceResult) => void;
  onDeleteSuccess?: () => void;
}

interface LeaderboardEntry extends RaceResult {
  riderName: string;
}

export function RaceResultLeaderboard({
  results,
  riders,
  eventName,
  showRecordButton = false,
  onRecordClick,
  onEditClick,
  onDeleteSuccess
}: RaceResultLeaderboardProps) {
  const { isAdmin, isCoach } = useAuth();
  const showToast = useUIStore(state => state.showToast);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Check if any results have lap times
  const hasLapTimes = results.some(r => 
    r.lap_time_1 || r.lap_time_2 || r.lap_time_3 || r.lap_time_4
  );

  // Add rider names to results
  const leaderboardEntries: LeaderboardEntry[] = results.map(result => {
    const rider = riders.find(r => r.rider_id === result.rider_id);
    return {
      ...result,
      riderName: rider?.name || result.rider_id
    };
  });

  // Sort by position (ascending), DNF riders always last
  const sortedEntries = [...leaderboardEntries].sort((a, b) => {
    const aDnf = !!a.dnf;
    const bDnf = !!b.dnf;
    if (aDnf !== bDnf) return aDnf ? 1 : -1;
    return a.position - b.position;
  });

  /**
   * Handle delete race result
   */
  const handleDelete = useCallback(async (result: LeaderboardEntry) => {
    if (!isAdmin && !isCoach) {
      showToast('error', 'Only admins and coaches can delete race results');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${result.riderName}'s result (${formatPosition(result.position, result.total_racers, result.dnf)})?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(`${result.event_id}_${result.rider_id}`);
      await restClient.deleteRaceResult(result.event_id, result.rider_id);
      showToast('success', 'Race result deleted successfully');
      
      // Notify parent to refresh
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    } catch (err) {
      console.error('Failed to delete race result:', err);
      showToast('error', 'Failed to delete race result. Please try again.');
    } finally {
      setDeletingId(null);
    }
  }, [isAdmin, isCoach, onDeleteSuccess, showToast]);

  return (
    <Card>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Race Results
            </h3>
            {eventName && (
              <p className="text-sm text-gray-600 mt-1">{eventName}</p>
            )}
            <p className="text-sm text-gray-500 mt-1">
              {sortedEntries.length} {sortedEntries.length === 1 ? 'participant' : 'participants'}
            </p>
          </div>
          
          {showRecordButton && (isAdmin || isCoach) && onRecordClick && (
            <Button onClick={onRecordClick} size="small">
              Record Result
            </Button>
          )}
        </div>

        {/* Leaderboard Table */}
        {sortedEntries.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No results recorded yet.</p>
            {showRecordButton && (isAdmin || isCoach) && (
              <p className="mt-2 text-sm">Click "Record Result" to add results.</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rider
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  {hasLapTimes && (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lap 1
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lap 2
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lap 3
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lap 4
                      </th>
                    </>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                  {(isAdmin || isCoach) && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedEntries.map((entry) => (
                  <tr key={`${entry.event_id}_${entry.rider_id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatPosition(entry.position, entry.total_racers, entry.dnf)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {entry.riderName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {entry.category || '-'}
                    </td>
                    {hasLapTimes && (
                      <>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {entry.lap_time_1 ? formatTime(entry.lap_time_1) : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {entry.lap_time_2 ? formatTime(entry.lap_time_2) : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {entry.lap_time_3 ? formatTime(entry.lap_time_3) : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {entry.lap_time_4 ? formatTime(entry.lap_time_4) : '-'}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {entry.total_time ? formatTime(entry.total_time) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                      {entry.notes || '-'}
                    </td>
                    {(isAdmin || isCoach) && (
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm space-x-2">
                        {onEditClick && (
                          <Button
                            onClick={() => onEditClick(entry)}
                            variant="secondary"
                            size="small"
                          >
                            Edit
                          </Button>
                        )}
                        <Button
                          onClick={() => handleDelete(entry)}
                          variant="danger"
                          size="small"
                          disabled={deletingId === `${entry.event_id}_${entry.rider_id}`}
                        >
                          {deletingId === `${entry.event_id}_${entry.rider_id}` ? 'Deleting...' : 'Delete'}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

